import React, { useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { ROUTES } from './config/routes';
import OfflineIndicator from './components/OfflineIndicator';

// Context
import { AuthContext } from './context/AuthContext';

// Components
import PageContainer from './components/PageContainer';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Home from './pages/Home';
import FindItems from './pages/FindItems';
import AddItemBox from './pages/AddItemBox';
import AddItem from './pages/AddItem';
import CreateBox from './pages/CreateBox';
import AllItems from './pages/AllItems';
import AllBoxes from './pages/AllBoxes';
import BoxDetail from './pages/BoxDetail';
import BoxDetailUUID from './pages/BoxDetailUUID';
import ItemEdit from './pages/ItemEdit';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import ProFeatures from './pages/ProFeatures';
import ComingSoon from './pages/ComingSoon';
import AcceptFamilyInvitation from './pages/AcceptFamilyInvitation';
import Profile from './pages/Profile';
import TagDashboard from './pages/TagDashboard';
import Locations from './pages/Locations';
import OfflineItems from './pages/OfflineItems';
import ImageTest from './pages/ImageTest';
import AssignQRCode from './pages/AssignQRCode';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DataDeletion from './pages/DataDeletion';
import AdminSupportTicket from './pages/AdminSupportTicket';
import AdminKB from './pages/AdminKB';
import AdminKBEdit from './pages/AdminKBEdit';
import LetGo from './pages/LetGo';
import ExitBin from './pages/ExitBin';

// Protected route wrapper
const ProtectedRoute = ({ children, showBottomNav = true }) => {
  const { currentUser, isLoading } = useContext(AuthContext);
  const location = useLocation();
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!currentUser) {
    // Store the intended destination before redirecting to login
    // Only store if not already set (to preserve original invitation path)
    const currentReturnPath = sessionStorage.getItem('returnPath');
    if (!currentReturnPath) {
      const returnPath = location.pathname + location.search;
      console.log('[ProtectedRoute] Storing returnPath:', returnPath, 'from pathname:', location.pathname);
      sessionStorage.setItem('returnPath', returnPath);
    } else {
      console.log('[ProtectedRoute] ReturnPath already set:', currentReturnPath, 'current pathname:', location.pathname);
    }
    
    // Preserve query parameters when redirecting to login (important for OAuth callbacks)
    return <Navigate to={`${ROUTES.LOGIN}${location.search}`} />;
  }
  
  return (
    <PageContainer showBottomNav={showBottomNav}>
      {children}
    </PageContainer>
  );
};

function App() {
  return (
    <>
      {/* Offline status indicator - visible on all pages */}
      <OfflineIndicator />
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path={ROUTES.REGISTER} element={<Login />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={`${ROUTES.RESET_PASSWORD}/:token`} element={<ResetPassword />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
      <Route path={`${ROUTES.VERIFY_EMAIL}/:token`} element={<VerifyEmail />} />
      <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
      
      {/* Protected routes */}
      <Route path={ROUTES.APP_HOME} element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.FIND} element={
        <ProtectedRoute>
          <FindItems />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ADD} element={
        <ProtectedRoute>
          <AddItemBox />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ADD_ITEM} element={
        <ProtectedRoute>
          <AddItem />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.CREATE_BOX} element={
        <ProtectedRoute>
          <CreateBox />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ALL_ITEMS} element={
        <ProtectedRoute>
          <AllItems />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.OFFLINE_ITEMS} element={
        <ProtectedRoute>
          <OfflineItems />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ALL_BOXES} element={
        <ProtectedRoute>
          <AllBoxes />
        </ProtectedRoute>
      } />
      
      <Route path={`${ROUTES.APP_HOME}/box/:boxId`} element={
        <ProtectedRoute>
          <BoxDetail />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.BOX_UUID_PATTERN} element={
        <ProtectedRoute>
          <BoxDetailUUID />
        </ProtectedRoute>
      } />
      
      <Route path={`${ROUTES.APP_HOME}/box/:boxId/edit`} element={
        <ProtectedRoute>
          <CreateBox isEditMode={true} />
        </ProtectedRoute>
      } />
      
      <Route path={`${ROUTES.APP_HOME}/item/:itemId`} element={
        <ProtectedRoute>
          <ItemEdit />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.SETTINGS} element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.SUBSCRIPTION} element={
        <ProtectedRoute>
          <Subscription />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.PRO_FEATURES} element={
        <ProtectedRoute>
          <ProFeatures />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.COMING_SOON} element={
        <ProtectedRoute>
          <ComingSoon />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.FAMILY_ACCEPT_PATTERN} element={
        <ProtectedRoute>
          <AcceptFamilyInvitation />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.PROFILE} element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.TAGS} element={
        <ProtectedRoute>
          <TagDashboard />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.LOCATIONS} element={
        <ProtectedRoute>
          <Locations />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.IMAGE_TEST} element={
        <ProtectedRoute>
          <ImageTest />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ASSIGN_QR} element={
        <ProtectedRoute>
          <AssignQRCode />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.LET_GO} element={
        <ProtectedRoute>
          <LetGo />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.EXIT_BIN} element={
        <ProtectedRoute>
          <ExitBin />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
      
      <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
      
      <Route path={ROUTES.DATA_DELETION} element={<DataDeletion />} />
      
      {/* Admin routes */}
      <Route path={ROUTES.ADMIN_SUPPORT_PATTERN} element={
        <ProtectedRoute showBottomNav={false}>
          <AdminSupportTicket />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ADMIN_KB} element={
        <ProtectedRoute showBottomNav={false}>
          <AdminKB />
        </ProtectedRoute>
      } />
      
      <Route path={ROUTES.ADMIN_KB_EDIT_PATTERN} element={
        <ProtectedRoute showBottomNav={false}>
          <AdminKBEdit />
        </ProtectedRoute>
      } />
      
      {/* Fallback route for app paths */}
      <Route path={`${ROUTES.APP_HOME}/*`} element={<Navigate to={ROUTES.APP_HOME} replace />} />
      
      {/* Root path redirect to landing page - handled by server */}
      <Route path="/" element={<Navigate to={ROUTES.APP_HOME} replace />} />
      
      {/* Catch all other paths and redirect to app */}
      <Route path="*" element={<Navigate to={ROUTES.APP_HOME} replace />} />
    </Routes>
    </>
  );
}

export default App;
